/**
 * Create Community Page
 * Multi-step form with accordion sections for creating a new community
 */

import { zodResolver } from "@hookform/resolvers/zod"
import type { AxiosError } from "axios"
import { ChevronsUpDown, MoreVertical, Plus, Trash2 } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { AutocompleteInput } from "@/components/forms/autoCompleteInput"
import { CustomInput } from "@/components/forms/customInput"
import { CustomTextarea } from "@/components/forms/customTextarea"
import { MultiSelectDropdown } from "@/components/forms/multiSelectDropdown"
import { TagsInput } from "@/components/forms/tagsInput"
import { MediaUploadSection } from "@/components/media/mediaUploadSection"
import { SelectCommunityUsersModal } from "@/components/modals/selectCommunityUsersModal"
import type { AccordionItem, AccordionRef } from "@/components/ui/accordion"
import { Accordion } from "@/components/ui/accordion"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuthStore } from "@/features/auth/store/authStore"
import { CommunitySuccessModal } from "@/features/communities/components/CommunitySuccessModal"
import { useCreateCommunity } from "@/features/communities/hooks/useCommunityMutations"
import { useCommunityRoles } from "@/features/communities/hooks/useCommunityRoles"
import {
  type CreateCommunityFormData,
  createCommunitySchema,
} from "@/features/communities/schemas/community.schema"
import type { AccessRole } from "@/features/communities/services/community.service"
import type { CommunityUserWithRole, LocationData } from "@/features/communities/types"
import {
  type SectionValidationResult,
  validateBasicSection,
  validateGuidelinesSection,
  validateMediaSection,
  validatePrivacySection,
  validateTagsSection,
  validateUsersSection,
} from "@/features/communities/utils/accordionValidation"
import { markImageUsed } from "@/features/media/services/ai-image.service"
import { useTranslation } from "@/language"

// Helper to convert uppercase to Capital Case
const toCapitalCase = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()

export function CreateCommunityScreen() {
  const { t } = useTranslation("communities")

  // Get current user from auth store
  const currentUser = useAuthStore(state => state.user)

  // Initialize React Hook Form with Zod validation
  const form = useForm<CreateCommunityFormData>({
    resolver: zodResolver(createCommunitySchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      handle: "",
      type: "public",
      isOrganization: false,
      address: "",
      description: "",
      websiteUrl: "",
      twitterUrl: "",
      instagramUrl: "",
      facebookUrl: "",
      guidelines: "",
      tags: [],
      privacySettings: {
        addToRituals: ["Moderators"],
        comment: ["Members"],
      },
      selectedUsers: [],
    },
  })

  const {
    register,
    formState: { errors, isValid },
    trigger,
    clearErrors,
    setValue,
    watch,
  } = form

  // Watch form values
  const formValues = watch()

  // Fetch community roles using React Query hook
  const { data: communityRoles = [] } = useCommunityRoles()

  // Get OWNER role ID from community roles
  const ownerRole = communityRoles.find(r => r.title === "OWNER")
  const ownerRoleId = ownerRole?.id

  // Role options for dropdowns - map from fetched roles
  const roleOptions = communityRoles.map(role => ({
    value: role.id,
    label: toCapitalCase(role.title),
  }))

  // Tags state management
  const [tags, setTags] = useState<string[]>([])

  // Community type state
  const [communityType, setCommunityType] = useState<"public" | "private">("public")

  // Privacy settings state - only keep addToRituals and comment
  const [privacySettings, setPrivacySettings] = useState({
    addToRituals: ["Moderators"],
    comment: ["Members"],
  })

  // Selected users for community
  const [selectedUsers, setSelectedUsers] = useState<CommunityUserWithRole[]>([])
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)

  // Initialize selectedUsers with current user as Owner once roles are loaded
  useEffect(() => {
    if (currentUser && ownerRoleId && communityRoles.length > 0) {
      // Only initialize if selectedUsers is empty (first load)
      if (selectedUsers.length === 0) {
        setSelectedUsers([
          {
            userId: currentUser.id,
            name: currentUser.name || null,
            email: currentUser.email,
            username: currentUser.username || null,
            profilePictureUrl:
              currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)?.filePath ||
              currentUser.profilePictureUrl ||
              null,
            roleIds: [ownerRoleId],
            roleId: ownerRoleId,
            communityBuilder: true,
            isCurrentUser: true,
          },
        ])
        setValue("selectedUsers", [
          {
            userId: currentUser.id,
            name: currentUser.name || null,
            email: currentUser.email,
            username: currentUser.username || null,
            profilePictureUrl:
              currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)?.filePath ||
              currentUser.profilePictureUrl ||
              null,
            roleIds: [ownerRoleId],
            roleId: ownerRoleId,
            communityBuilder: true,
            isCurrentUser: true,
          },
        ])
      }
    }
  }, [currentUser, ownerRoleId, communityRoles.length, selectedUsers.length, setValue])

  // Selected location data
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(null)

  // Media state
  const [mediaFiles, setMediaFiles] = useState<{
    images: Array<{ id: string; file?: File; src?: string; isCover: boolean }>
    videos: Array<{ id: string; file?: File; src?: string }>
    aiGenerationId?: string | null
    aiGenerationImageIndex?: number | null
  }>({ images: [], videos: [] })

  // Video URL state
  // Video URLs with thumbnails - for multiple videos
  interface VideoUrlEntry {
    id: string
    url: string
    thumbnail: string
    title?: string
    isInvalid?: boolean
  }
  const [videoUrls, setVideoUrls] = useState<VideoUrlEntry[]>([])

  // Section validation state
  const [sectionValidation, setSectionValidation] = useState<
    Record<string, SectionValidationResult>
  >({})

  // Loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mediaError, setMediaError] = useState<string | null>(null)

  // Success modal state
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)
  const [createdCommunityId, setCreatedCommunityId] = useState<string | null>(null)

  // Track which sections have been attempted (opened at least once)
  const [attemptedSections, setAttemptedSections] = useState<Set<string>>(
    new Set(["community-info"]),
  )

  // Navigation hook
  const navigate = useNavigate()

  // Create community mutation with automatic redirect and cache invalidation
  const createCommunityMutation = useCreateCommunity()

  // Check if form is valid for enabling/disabling submit button
  const isFormDisabled = !isValid

  // Handlers
  const handleAddTag = (tag: string) => {
    const newTags = [...tags, tag]
    setTags(newTags)
    setValue("tags", newTags)
    trigger("tags")
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue("tags", updatedTags)
    trigger("tags")
  }

  // Handle adding users from modal
  const handleAddUsers = (users: CommunityUserWithRole[]) => {
    // Filter out current user if already present (they're added by default as Owner)
    const newUsers = users.filter(u => u.userId !== currentUser?.id)
    const updated = [...selectedUsers, ...newUsers]
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  // Handle removing a user - prevent removing current user (Owner)
  const handleRemoveUser = (userId: string) => {
    // Don't allow removing the current user (Owner)
    if (userId === currentUser?.id) {
      return
    }
    const updated = selectedUsers.filter(u => u.userId !== userId)
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
    trigger("selectedUsers")
  }

  // Handle updating user roles - prevent changing current user's role
  const handleUpdateUserRoles = (userId: string, newRoleIds: string[]) => {
    // Don't allow changing the current user's (Owner) role
    if (userId === currentUser?.id) {
      return
    }
    const updated = selectedUsers.map(u =>
      u.userId === userId ? { ...u, roleIds: newRoleIds } : u,
    )
    setSelectedUsers(updated)
    setValue("selectedUsers", updated)
  }

  // Watch individual form values for validation
  const nameValue = watch("name")
  const handleValue = watch("handle")
  const typeValue = watch("type")
  const descriptionValue = watch("description")
  const guidelinesValue = watch("guidelines")
  const websiteUrlValue = watch("websiteUrl")
  const instagramUrlValue = watch("instagramUrl")
  const facebookUrlValue = watch("facebookUrl")
  const twitterUrlValue = watch("twitterUrl")

  // Validate all accordion sections on form change
  // biome-ignore lint/correctness/useExhaustiveDependencies: We intentionally watch individual values to trigger validation
  useEffect(() => {
    const formValues = watch()

    const validation: Record<string, SectionValidationResult> = {
      "community-info": validateBasicSection(formValues, selectedLocation),
      description: validateTagsSection(formValues, selectedLocation), // Tags are in description accordion
      "upload-media": validateMediaSection(mediaFiles),
      "users-roles": validateUsersSection(formValues, selectedUsers),
      privacy: validatePrivacySection(formValues),
      guidelines: validateGuidelinesSection(formValues),
    }

    setSectionValidation(validation)
  }, [
    nameValue,
    handleValue,
    typeValue,
    descriptionValue,
    guidelinesValue,
    websiteUrlValue,
    instagramUrlValue,
    facebookUrlValue,
    twitterUrlValue,
    selectedUsers,
    mediaFiles,
    selectedLocation,
  ])

  // Accordion items data
  const accordionItems: AccordionItem[] = [
    {
      id: "community-info",
      title: t("createCommunity.communityInfo"),
      showNextButton: true,
      isComplete: sectionValidation["community-info"]?.isComplete,
      hasError: !sectionValidation["community-info"]?.isComplete,
      summary: sectionValidation["community-info"]?.summary,
      displayName: sectionValidation["community-info"]?.displayName,
      content: (
        <div className="space-y-4 mb-5">
          <p className="text-sm mb-7 mt-2 ms-1 text-body">{t("createCommunity.basicInfo")}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
            {/* Name Field */}
            <CustomInput
              {...register("name")}
              id="community-name"
              label={t("createCommunity.name")}
              required
              placeholder={t("createCommunity.namePlaceholder")}
              error={attemptedSections.has("community-info") ? errors.name?.message : undefined}
              onClearError={() => clearErrors("name")}
            />

            {/* Community Handle Field */}
            <CustomInput
              {...register("handle")}
              id="community-handle"
              label={t("createCommunity.handle")}
              required
              prefix="@"
              placeholder={t("createCommunity.handlePlaceholder")}
              error={attemptedSections.has("community-info") ? errors.handle?.message : undefined}
              onClearError={() => clearErrors("handle")}
            />
          </div>

          <div className="flex items-center gap-3 ms-1">
            <Checkbox
              id="is-organization"
              checked={watch("isOrganization")}
              onCheckedChange={checked => setValue("isOrganization", checked === true)}
              className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
            />
            <label
              htmlFor="is-organization"
              className="text-sm text-body cursor-pointer select-none"
            >
              This community represents a company or organization
            </label>
          </div>
        </div>
      ),
    },
    {
      id: "description",
      title: t("createCommunity.description"),
      showNextButton: true,
      isComplete: sectionValidation.description?.isComplete,
      hasError: !sectionValidation.description?.isComplete,
      summary: sectionValidation.description?.summary,
      content: (
        <div className="space-y-6 mb-5">
          {/* Section 1: Description */}
          <div>
            <p className="text-[14px] font-monteserat mb-7 mt-2 text-body">
              {t("createCommunity.descriptionOverview")}
            </p>
            <CustomTextarea
              {...register("description")}
              id="community-description"
              label={t("createCommunity.description")}
              required
              className="leading-[25px]"
              placeholder={t("createCommunity.descriptionPlaceholder")}
              error={attemptedSections.has("description") ? errors.description?.message : undefined}
              onClearError={() => clearErrors("description")}
              rows={4}
            />
          </div>

          {/* Section 2: Tags */}
          <div>
            <TagsInput
              id="community-tags"
              label={t("createCommunity.tags")}
              tags={tags}
              placeholder={t("createCommunity.placeholders.tags")}
              required={true}
              onAddTag={handleAddTag}
              onRemoveTag={handleRemoveTag}
              error={attemptedSections.has("description") ? errors.tags?.message : undefined}
              onClearError={() => clearErrors("tags")}
              enableSearch={true}
            />
          </div>

          {/* Section 3: Location */}
          <div>
            <AutocompleteInput
              label={t("createCommunity.address")}
              value={watch("address") || ""}
              mode="full"
              onLocationSelect={locationData => {
                setValue("address", locationData.address)
                setSelectedLocation(locationData)
              }}
              onInputChange={value => {
                setValue("address", value)
              }}
              placeholder={t("createCommunity.addressPlaceholder")}
              error={attemptedSections.has("description") ? errors.address?.message : undefined}
              onClearError={() => clearErrors("address")}
            />
          </div>

          {/* Section 3: URLs - 2 Column Grid */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <CustomInput
                {...register("websiteUrl")}
                id="website-url"
                label={t("createCommunity.website")}
                placeholder={t("createCommunity.websitePlaceholder")}
                error={
                  attemptedSections.has("description") ? errors.websiteUrl?.message : undefined
                }
                onClearError={() => clearErrors("websiteUrl")}
              />
              <CustomInput
                {...register("twitterUrl")}
                id="twitter-url"
                label={t("createCommunity.twitter")}
                placeholder={t("createCommunity.twitterPlaceholder")}
                error={
                  attemptedSections.has("description") ? errors.twitterUrl?.message : undefined
                }
                onClearError={() => clearErrors("twitterUrl")}
              />
              <CustomInput
                {...register("instagramUrl")}
                id="instagram-url"
                label={t("createCommunity.instagram")}
                placeholder={t("createCommunity.instagramPlaceholder")}
                error={
                  attemptedSections.has("description") ? errors.instagramUrl?.message : undefined
                }
                onClearError={() => clearErrors("instagramUrl")}
              />
              <CustomInput
                {...register("facebookUrl")}
                id="facebook-url"
                label={t("createCommunity.facebook")}
                placeholder={t("createCommunity.facebookPlaceholder")}
                error={
                  attemptedSections.has("description") ? errors.facebookUrl?.message : undefined
                }
                onClearError={() => clearErrors("facebookUrl")}
              />
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "upload-media",
      title: t("createCommunity.uploadMedia"),
      showNextButton: true,
      isComplete: sectionValidation["upload-media"]?.isComplete,
      hasError: !sectionValidation["upload-media"]?.isComplete,
      summary: sectionValidation["upload-media"]?.summary,
      content: (
        <div className="space-y-4">
          <p className="text-[14px] ms-1 font-monteserat loading-[20px] text-body mb-5 mt-2">
            {t("createCommunity.uploadMediaDescription")}
          </p>
          <MediaUploadSection
            onMediaChange={media => {
              setMediaFiles(media)
              if (media.images.length > 0) {
                setMediaError(null)
              }
            }}
            onVideoUrlsChange={videos => setVideoUrls(videos)}
            showVideoSection={true}
            showVideoUrlInput={true}
            maxFiles={5}
            maxFileSize={5 * 1024 * 1024}
            allowedTypes={["image/png", "image/jpeg", "image/jpg"]}
            enableAiGeneration
            aiEntityType="community"
            aiEntityContext={{
              name: nameValue,
              description: descriptionValue,
              type: typeValue,
            }}
          />
          {attemptedSections.has("upload-media") && mediaError && (
            <p className="text-xs text-brand-terracotta-bright mt-1">{mediaError}</p>
          )}
        </div>
      ),
    },
    {
      id: "users-roles",
      title: t("createCommunity.moderatorsRoles"),
      showNextButton: true,
      isComplete: sectionValidation["users-roles"]?.isComplete,
      hasError: !sectionValidation["users-roles"]?.isComplete,
      summary: sectionValidation["users-roles"]?.summary,
      userPreviews: sectionValidation["users-roles"]?.userPreviews,
      totalCount: selectedUsers.length,
      content: (
        <div className="space-y-6">
          {/* Subtitle */}
          <p className="text-body mt-2">{t("createCommunity.moderatorsRolesDescription")}</p>

          {/* Selected Users Display */}
          {selectedUsers.length > 0 && (
            <div className="space-y-6">
              {selectedUsers.map(user => (
                <div key={user.userId} className="flex items-center gap-3">
                  {/* Avatar - 48px with beige background */}
                  <div className="w-12 h-12 rounded-full bg-themed flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {user.profilePictureUrl ? (
                      <img
                        src={user.profilePictureUrl}
                        alt={user.name || user.email}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="font-semibold">
                        {(user.name || user.email)[0].toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">
                      {user.name || t("createCommunity.noName")}
                    </p>
                    <p className="text-sm truncate">
                      {user.username ? `@${user.username}` : user.email}
                    </p>
                  </div>

                  {/* Role Multi-Select - Show disabled dropdown for current user (Owner) */}
                  {user.isCurrentUser ? (
                    <div className="md:min-w-[200px] font-bold rounded-lg text-[14px] text-primary text-left focus:outline-none flex items-center justify-end gap-3 px-3 py-1.5 opacity-50 cursor-not-allowed">
                      <span className="truncate text-primary">Owner</span>
                      <ChevronsUpDown size={16} strokeWidth={2.5} />
                    </div>
                  ) : (
                    <MultiSelectDropdown
                      options={roleOptions.map(o => o.label)}
                      selectedOptions={
                        user.roleIds && user.roleIds.length > 0
                          ? roleOptions
                              .filter(o => user.roleIds.includes(o.value))
                              .map(o => o.label)
                          : ["Ritualist"]
                      }
                      onChange={labels => {
                        const selectedRoleIds = roleOptions
                          .filter(o => labels.includes(o.label))
                          .map(o => o.value)
                        handleUpdateUserRoles(user.userId, selectedRoleIds)
                      }}
                    />
                  )}

                  {/* Action Icons - Hide for current user (Owner) */}
                  {!user.isCurrentUser && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                          <MoreVertical size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleRemoveUser(user.userId)}
                          className="text-red focus:text-red"
                        >
                          <Trash2 size={16} />
                          {t("createCommunity.removeUser")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            {/* Invite Organizer Button */}
            <Button
              variant="ghost"
              onClick={() => setIsUserModalOpen(true)}
              className="flex items-center gap-2 px-3 h-8 rounded-lg text-sm font-medium text-primary"
            >
              <Plus size={18} />
              {t("createCommunity.addMembers")}
            </Button>
          </div>

          {/* Error Message */}
          {attemptedSections.has("users-roles") && errors.selectedUsers && (
            <p className="text-xs text-brand-terracotta-bright mt-1">
              {errors.selectedUsers.message}
            </p>
          )}
        </div>
      ),
    },
    {
      id: "guidelines",
      title: t("createCommunity.guidelines"),
      showNextButton: true,
      isComplete: sectionValidation.guidelines?.isComplete,
      hasError: !sectionValidation.guidelines?.isComplete,
      summary: sectionValidation.guidelines?.summary,
      content: (
        <div className="space-y-4 mb-5">
          <p className="text-sm mb-7 text-body mt-2">
            {t("createCommunity.guidelinesDescription")}
          </p>
          <CustomTextarea
            {...register("guidelines")}
            id="community-guidelines"
            label={t("createCommunity.guidelines")}
            required
            placeholder={t("createCommunity.guidelinesPlaceholder")}
            error={attemptedSections.has("guidelines") ? errors.guidelines?.message : undefined}
            onClearError={() => clearErrors("guidelines")}
            rows={4}
          />
        </div>
      ),
    },
    {
      id: "privacy",
      title: t("createCommunity.privacy"),
      showNextButton: true,
      isComplete: sectionValidation.privacy?.isComplete,
      hasError: !sectionValidation.privacy?.isComplete,
      summary: sectionValidation.privacy?.summary,
      content: (
        <div className="space-y-3">
          <p className="text-body mt-[6px] text-[14px] loading-[20px]">
            {t("createCommunity.privacyDescription")}
          </p>

          {/* Community Type Selection */}
          <div className="flex text=[12px] items-center justify-between">
            <label className="font-semibold text-primary block">{t("createCommunity.type")}</label>
            <Select
              value={communityType}
              onValueChange={value => setCommunityType(value as "public" | "private")}
            >
              <SelectTrigger className="font-semibold text-[14px] p-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Public - Anyone can join</SelectItem>
                <SelectItem value="private">Private - Invite only / Request to join</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Privacy Options - 2 Multi-Select Dropdowns */}
          <div className="space-y-6">
            {/* Option 1: Who can add/accept rituals to community */}
            <MultiSelectDropdown
              label={t("createCommunity.whoCanAddToRituals")}
              options={["Moderators", "Ritualists", "Members"]}
              selectedOptions={privacySettings.addToRituals}
              onChange={options =>
                setPrivacySettings(prev => ({
                  ...prev,
                  addToRituals: options,
                }))
              }
            />

            {/* Option 2: Who can comment */}
            <MultiSelectDropdown
              label={t("createCommunity.whoCanComment")}
              options={["Moderators", "Ritualists", "Members"]}
              selectedOptions={privacySettings.comment}
              onChange={options => setPrivacySettings(prev => ({ ...prev, comment: options }))}
            />
          </div>
        </div>
      ),
    },
  ]

  const accordionRef = useRef<AccordionRef>(null)

  // Handle accordion open change - track attempted sections
  const handleAccordionOpenChange = useCallback((openItems: Set<string>) => {
    if (openItems.size > 0) {
      setAttemptedSections(prev => new Set([...prev, ...openItems]))
    }
  }, [])

  const handleNext = async (currentIndex: number): Promise<boolean> => {
    const currentAccordionId = accordionItems[currentIndex].id

    // Determine which fields to validate based on current accordion
    let fieldsToValidate: (keyof CreateCommunityFormData)[] = []

    switch (currentAccordionId) {
      case "community-info":
        fieldsToValidate = ["name", "handle"]
        break
      case "description":
        fieldsToValidate = ["description", "tags", "address"]
        break
      case "upload-media": {
        // Custom validation for images (not in form schema)
        if (mediaFiles.images.length === 0) {
          setMediaError(t("createCommunity.imageRequired"))
          return false
        }

        // Check if cover image is selected
        const hasCover = mediaFiles.images.some(img => img.isCover)
        if (!hasCover) {
          setMediaError(t("createCommunity.selectCoverImage"))
          return false
        }

        setMediaError(null) // Clear error if valid
        return true
      }
      case "users-roles":
        fieldsToValidate = ["selectedUsers"]
        break
      case "guidelines":
        fieldsToValidate = ["guidelines"]
        break
      default:
        // No validation needed for other accordions
        // Allow navigation to proceed
        return true
    }

    // Trigger validation for specific fields
    const isValid = await trigger(fieldsToValidate)

    // Return true if valid (allow navigation), false if invalid (prevent navigation)
    // Accordion component will check this return value and only switch accordions if true
    return isValid
  }

  const handleCreateCommunity = async () => {
    // Trigger full form validation
    const isFormValid = await trigger()

    if (!isFormValid) {
      setError(t("createCommunity.validationError"))
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      // Transform privacy settings from frontend arrays to backend arrays
      const privacyMap: Record<string, string> = {
        Moderators: "MODERATORS",
        Ritualists: "RITUALISTS",
        Members: "MEMBERS",
      }

      const transformArray = (arr: string[]): AccessRole[] => {
        return arr.map(role => (privacyMap[role] || "MEMBERS") as AccessRole)
      }

      // Prepare users array with role IDs (support both role_id for backward compatibility and role_ids for multi-role)
      const users = selectedUsers.map(u => {
        const roleIdsArray =
          u.roleIds && u.roleIds.length > 0 ? u.roleIds : u.roleId ? [u.roleId] : []
        return {
          user_id: u.userId,
          role_ids: roleIdsArray,
        }
      })

      // Find cover image index
      const coverImageIndex = mediaFiles.images.findIndex(img => img.isCover)
      const coverIndex = coverImageIndex >= 0 ? coverImageIndex : undefined

      // Prepare files array (filter out undefined - shouldn't happen in create flow)
      const files = mediaFiles.images
        .map(img => img.file)
        .filter((file): file is File => file !== undefined)

      // Create community request using formValues from React Hook Form
      const request = {
        name: formValues.name,
        community_handle: formValues.handle,
        type: communityType.toUpperCase() as "PUBLIC" | "PRIVATE",
        is_organization: formValues.isOrganization,
        street_address: formValues.address || undefined,
        city: selectedLocation?.city,
        state: selectedLocation?.state,
        country: selectedLocation?.country,
        zip_code: selectedLocation?.zipCode,
        lat: selectedLocation?.lat,
        long: selectedLocation?.lng,
        description: formValues.description,
        tags: tags,
        website_url: formValues.websiteUrl || undefined,
        twitter_url: formValues.twitterUrl || undefined,
        instagram_url: formValues.instagramUrl || undefined,
        facebook_url: formValues.facebookUrl || undefined,
        videos:
          videoUrls.length > 0
            ? videoUrls.map(v => ({
                video_url: v.url,
                thumbnail_url: v.thumbnail,
                title: v.title || "",
              }))
            : undefined,
        guidelines: formValues.guidelines,
        who_can_add_community_to_rituals: transformArray(privacySettings.addToRituals),
        who_can_comment: transformArray(privacySettings.comment),
        users: users.length > 0 ? users : undefined,
        files: files.length > 0 ? files : undefined,
        coverImageIndex: coverIndex,
      }

      // Use mutation hook - handles cache invalidation
      const result = await createCommunityMutation.mutateAsync(request)

      // Mark AI image as used if one was selected
      if (
        mediaFiles.aiGenerationId &&
        mediaFiles.aiGenerationImageIndex !== null &&
        mediaFiles.aiGenerationImageIndex !== undefined
      ) {
        markImageUsed(mediaFiles.aiGenerationId, mediaFiles.aiGenerationImageIndex).catch(() => {})
      }

      // Show success toast
      toast.success(t("createCommunity.successMessage"))

      // Open success modal with community details
      if (result?.id) {
        setCreatedCommunityId(result.id)
        setIsSuccessModalOpen(true)
      }
    } catch (err: unknown) {
      const error = err as AxiosError<{ message: string }>
      const errorMessage = error.response?.data?.message || t("createCommunity.errorMessage")

      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background ">
      <div className="max-w-360 mx-auto px-5 md:px-17.5 py-10">
        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Accordions (70%) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Accordion Sections */}
            <Accordion
              ref={accordionRef}
              items={accordionItems}
              defaultOpenIndex={0}
              allowMultiple={false}
              onNextClick={handleNext}
              onOpenChange={handleAccordionOpenChange}
              onSectionAttempt={sectionId => {
                setAttemptedSections(prev => new Set([...prev, sectionId]))
              }}
              attemptedSections={attemptedSections}
            />
          </div>

          {/* Right Column - Sidebar (30%) */}
          <div className="space-y-4">
            {/* Tips Card */}
            <div className="rounded-xl border-1 bg-card border-card pt-4 pb-3 px-5">
              <h3 className="text-[18px] loading-[25px] font-montserat font-semibold mb-3 text-primary">
                {t("createCommunity.tips")}
              </h3>
              <ul className="space-y-1 text-sm p-2">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span className="text-primary text-[14px] loading-[25px]">
                    {t("createCommunity.tip1")}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span className="text-primary text-[14px] loading-[25px]">
                    {t("createCommunity.tip2")}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span className="text-primary text-[14px] loading-[25px]">
                    {t("createCommunity.tip3")}
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span className="text-primary text-[14px] loading-[25px]">
                    {t("createCommunity.tip4")}
                  </span>
                </li>
              </ul>
            </div>

            {/* Action Card */}
            <div className="border bg-card border-card rounded-xl p-5">
              {/* Error Message */}
              {error && (
                <div className="mb-4 p-3 bg-transparent border border-brand-terracotta-bright rounded-md">
                  <p className="text-sm text-brand-terracotta-bright">{error}</p>
                </div>
              )}

              {/* Create Community Button */}
              <Button
                onClick={handleCreateCommunity}
                disabled={isFormDisabled}
                className="w-full uppercase text-sm"
                variant="secondary"
              >
                {isSubmitting ? t("createCommunity.creating") : t("createCommunity.createButton")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* User Selection Modal */}
      <SelectCommunityUsersModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        onAddUsers={handleAddUsers}
        existingUserIds={selectedUsers.map(u => u.userId)}
        communityRoles={communityRoles}
        currentUser={
          currentUser
            ? {
                id: currentUser.id,
                name: currentUser.name,
                email: currentUser.email,
                username: currentUser.username,
                profilePictureUrl:
                  currentUser.profile_photos?.find(p => p.isCover || p.is_cover_image)?.filePath ||
                  currentUser.profilePictureUrl,
                profilePhotos: currentUser.profile_photos,
              }
            : undefined
        }
        ownerRoleId={ownerRoleId}
      />

      {/* Success Modal */}
      <CommunitySuccessModal
        overlayClass="bg-[rgb(0,0,0)]"
        isOpen={isSuccessModalOpen}
        communityId={createdCommunityId || ""}
        onViewCommunity={() => {
          if (createdCommunityId) {
            navigate(`/communities/${createdCommunityId}`)
          }
        }}
      />
    </div>
  )
}
